// pages/blog/[uid].js

import React, { Fragment, useEffect } from 'react';
import { createClient } from '../../prismicio';
import { ThemeProvider } from 'styled-components';
import Head from 'next/head';
import Sticky from 'react-stickynode';
import { DrawerProvider } from 'common/contexts/DrawerContext';
import { saasModernTheme } from 'common/theme/saasModern';
import ResetCSS from 'common/assets/css/style';
import { GlobalStyle, ContentWrapper } from 'containers/SaasModern/sassModern.style';
import Navbar from 'containers/SaasModern/Navbar';
import Footer from 'containers/SaasModern/Footer';
import Blog from 'containers/SaasModern/Blog';
import { Mixpanel } from 'utils/mixpanel';
import MetaHead from 'common/components/MetaHead';
import { getPrismicLanguage } from 'utils/getPrismicLanguage';

export default function Page({ page, author, locale }) {
  useEffect(() => {
    Mixpanel.track('Article view', {
      'Article title': page?.data?.title,
    });
  }, []);

  const data = page?.data;
  return (
    <ThemeProvider theme={saasModernTheme}>
      <Fragment>
        <MetaHead
          title={page?.data?.meta_title}
          description={page?.data?.meta_description}
          keywords={page?.data?.meta_keywords}
          imageUrl={page?.data?.meta_image?.url}
          canonicalUrl={page?.data?.meta_canonique.url}
        />

        <ResetCSS />
        <GlobalStyle />

        <ContentWrapper>
          <Sticky top={0} innerZ={9999} activeClass="sticky-nav-active">
            <DrawerProvider>
              <Navbar locale={locale} />
            </DrawerProvider>
          </Sticky>
          <Blog data={data} author={author} />
          <Footer locale={locale} />
        </ContentWrapper>
      </Fragment>
    </ThemeProvider>
  );
}

export async function getStaticPaths() {
  const client = createClient();

  try {
    const articles = await client.getAllByType('article', {
      lang: '*',
      pageSize: 10,
      orderings: [{ field: 'my.article.published_date', direction: 'desc' }],
    });

    const paths = articles.map((page) => {
      return {
        params: { uid: page.uid },
        locale: page.lang === 'en-us' ? 'en-us' : page.lang,
      };
    });

    return {
      paths,
      fallback: 'blocking',
    };
  } catch (error) {
    console.error('Error fetching articles for static paths:', error);
    return {
      paths: [],
      fallback: 'blocking',
    };
  }
}

export async function getStaticProps({ params, previewData, locale }) {
  const client = createClient({ previewData });
  const prismicLanguage = getPrismicLanguage(locale);

  try {
    let latestArticles = [];
    try {
      latestArticles = await client.getAllByType('article', {
        lang: prismicLanguage,
        limit: 3,
        orderings: [{ field: 'my.article.published_date', direction: 'desc' }],
      }) || [];
    } catch (error) {
      console.warn('Latest articles not found, using empty array as fallback');
    }

    const page = await client.getByUID('article', params.uid, {
      lang: prismicLanguage,
    });

    if (!page) {
      return { notFound: true };
    }

    let author = {};
    try {
      if (page.data?.author?.uid) {
        author = await client.getByUID('author', page.data.author.uid, {
          lang: prismicLanguage,
        }) || {};
      }
    } catch (error) {
      console.warn('Author not found, using empty object as fallback');
      author = {};
    }

    return {
      props: {
        page: page || {},
        latestArticles: latestArticles || [],
        author: author || {},
        locale,
      },
      revalidate: 60, 
    };
  } catch (error) {
    console.error('Error fetching document:', error);
    return {
      notFound: true,
    };
  }
}
